import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Alert, Snackbar } from '@mui/material';

export type ToastSeverity = 'success' | 'error' | 'warning' | 'info';

interface ToastMessage {
  id: number;
  message: string;
  severity: ToastSeverity;
}

interface ToastContextValue {
  /** Generic entry point — prefer the severity-specific helpers below. */
  showToast: (message: string, severity?: ToastSeverity) => void;
  showSuccess: (message: string) => void;
  showError: (message: string) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

let nextToastId = 1;

/**
 * Single, app-wide toast/snackbar system.
 *
 * Before this, ~44 pages each carried their own copy of:
 *   const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })
 *   ...plus a close handler, plus the <Snackbar><Alert> JSX at the bottom.
 * That's the same ~15 lines of boilerplate duplicated everywhere, and every
 * copy is a place a future fix (e.g. changing the auto-hide duration, or
 * queueing so a second toast doesn't just replace the first) has to be
 * applied 44 times instead of once.
 *
 * Usage: `const { showSuccess, showError } = useToast();` — call whichever
 * fits from a submit handler's success/catch branch. Multiple toasts queue
 * and show one at a time rather than overwriting each other.
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [queue, setQueue] = useState<ToastMessage[]>([]);
  const [current, setCurrent] = useState<ToastMessage | null>(null);
  const [open, setOpen] = useState(false);

  const showToast = useCallback(
    (message: string, severity: ToastSeverity = 'info') => {
      setQueue(prev => [...prev, { id: nextToastId++, message, severity }]);
    },
    []
  );

  const showSuccess = useCallback(
    (message: string) => showToast(message, 'success'),
    [showToast]
  );
  const showError = useCallback(
    (message: string) => showToast(message, 'error'),
    [showToast]
  );
  const showWarning = useCallback(
    (message: string) => showToast(message, 'warning'),
    [showToast]
  );
  const showInfo = useCallback(
    (message: string) => showToast(message, 'info'),
    [showToast]
  );

  // Pull the next queued toast in once the current one has finished closing.
  React.useEffect(() => {
    if (!current && queue.length > 0) {
      setCurrent(queue[0]);
      setQueue(prev => prev.slice(1));
      setOpen(true);
    }
  }, [queue, current]);

  const handleClose = useCallback(
    (_event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') return;
      setOpen(false);
    },
    []
  );

  const handleExited = useCallback(() => {
    setCurrent(null);
  }, []);

  const value = useMemo(
    () => ({ showToast, showSuccess, showError, showWarning, showInfo }),
    [showToast, showSuccess, showError, showWarning, showInfo]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        key={current?.id}
        open={open}
        autoHideDuration={5000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionProps={{ onExited: handleExited }}
      >
        {current ? (
          <Alert
            onClose={handleClose}
            severity={current.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {current.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast() must be used within a <ToastProvider>');
  }
  return ctx;
}
